/**
 * HartaPusakaTab Component
 * Tab for entering estate assets (harta pusaka) in the Faraid calculator
 * 
 * Reference: FaraidInfo.md Section 2.1 (Rukun Pusaka - Harta)
 */

import { useCallback, useMemo } from 'react';
import { Card } from '@components/common/Card';
import { Button } from '@components/common/Button';
import type { AssetsState, AssetType } from '../../types';
import { ASSET_TYPES, ASSET_TYPE_LABELS } from '../../types';
import styles from './HartaPusakaTab.module.css';

interface HartaPusakaTabProps {
  assets: AssetsState;
  onAssetValueChange: (assetType: AssetType, value: number) => void;
}

/**
 * Asset row component for individual asset input
 */
const AssetRow = ({
  assetType,
  value,
  onChange,
}: {
  assetType: AssetType;
  value: number;
  onChange: (assetType: AssetType, value: number) => void;
}) => {
  const label = ASSET_TYPE_LABELS[assetType];
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(inputValue) || 0;
    onChange(assetType, Math.max(0, numValue));
  }, [assetType, onChange]);
  
  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(inputValue) || 0;
    onChange(assetType, Math.max(0, numValue));
  }, [assetType, onChange]);
  
  return (
    <div className={styles.assetRow}>
      <div className={styles.assetLabel}>
        <span className={styles.assetName}>{label.ms}</span>
      </div>
      <div className={styles.assetValue}>
        <input
          type="text"
          inputMode="decimal"
          className={styles.valueInput}
          value={value === 0 ? '' : value.toString()}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="0"
          aria-label={`Nilai ${label.ms}`}
        />
      </div>
    </div>
  );
};

/**
 * HartaPusakaTab main component
 */
export const HartaPusakaTab = ({
  assets,
  onAssetValueChange,
}: HartaPusakaTabProps) => {
  // Get asset value by type
  const getAssetValue = useCallback((type: AssetType): number => {
    const asset = assets.assets.find(a => a.type === type);
    return asset?.value || 0;
  }, [assets.assets]);
  
  // Calculate total
  const totalValue = useMemo(() => {
    return assets.assets.reduce((sum, asset) => sum + asset.value, 0);
  }, [assets.assets]);
  
  // Handle save as image (placeholder)
  const handleSaveAsImage = useCallback(() => {
    // TODO: Implement save as image functionality
    alert('Fungsi simpan sebagai gambar akan dilaksanakan.');
  }, []);
  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSaveAsImage}
          className={styles.saveButton}
        >
          Save as image
        </Button>
      </div>
      
      <Card className={styles.card}>
        {/* Header */}
        <div className={styles.tableHeader}>
          <div className={styles.headerLabel}>Jenis Harta</div>
          <div className={styles.headerValue}>Nilai (RM)</div>
        </div>
        
        {/* Asset Rows */}
        <div className={styles.tableBody}>
          {ASSET_TYPES.map((assetType) => (
            <AssetRow
              key={assetType}
              assetType={assetType}
              value={getAssetValue(assetType)}
              onChange={onAssetValueChange}
            />
          ))}
        </div>
        
        {/* Total Row */}
        <div className={styles.totalRow}>
          <div className={styles.totalLabel}>JUMLAH</div>
          <div className={styles.totalValue}>
            {totalValue.toLocaleString('ms-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </Card>
      
      {/* Info section */}
      <div className={styles.infoSection}>
        <h4 className={styles.infoTitle}>Nota Penting:</h4>
        <ul className={styles.infoList}>
          <li>
            Harta pusaka termasuk: Semua harta si mati yang diperolehi secara halal, 
            simpanan KWSP, Tabung Haji, dan penama Takaful.
          </li>
          <li>
            Bukan harta pusaka: Harta yang telah dijual, diwakaf, dihibah, khairat kematian, 
            pencen terbitan, dan bayaran PERKESO.
          </li>
          <li>
            Sebelum pembahagian, perlu ditolak: Kos pengurusan jenazah, hutang-piutang si mati, 
            tuntutan harta sepencarian, dan wasiat (jika ada).
          </li>
        </ul>
      </div>
    </div>
  );
};
